'use client';

import React from 'react';
import { cn, focusRing } from '@utils';
import { tv } from 'tailwind-variants';

import { Error, Information, Success, Warning } from '../icons';

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: 'info' | 'warning' | 'error' | 'success';
}

const alert = tv({
	extend: focusRing,
	base: ['bg-neutral-white text-light-text flex flex-row rounded-[3px] border-2'],
	variants: {
		variant: {
			info: 'border-status-info',
			warning: 'border-status-caution',
			error: 'border-status-error',
			success: 'border-status-success',
		},
	},
	defaultVariants: {
		variant: 'info',
	},
});

const iconContainer = tv({
	base: ['flex w-12 min-w-12 items-center justify-center'],
	variants: {
		variant: {
			info: 'bg-status-info',
			warning: 'bg-status-caution',
			error: 'bg-status-error',
			success: 'bg-status-success',
		},
	},
	defaultVariants: {
		variant: 'info',
	},
});

const getAlertAriaLabel = (variant: AlertProps['variant']) => {
	switch (variant) {
		case 'info':
			return 'Information';
		case 'warning':
			return 'Warning';
		case 'error':
			return 'Error';
		case 'success':
			return 'Success';
		default:
			return 'Information';
	}
};

const getAlertIcon = (variant: AlertProps['variant']) => {
	switch (variant) {
		case 'error':
			return <Error className="size-icon-sm text-neutral-white" />;
		case 'success':
			return <Success className="size-icon-sm text-neutral-white" />;
		case 'info':
			return <Information className="size-icon-sm text-neutral-white" />;
		case 'warning':
			return <Warning className="size-icon-sm text-neutral-black" />;
		default:
			return <Information className="size-icon-sm text-neutral-white" />;
	}
};

const Alert = ({ className, variant, children, ...props }: AlertProps) => {
	const ariaLabel = getAlertAriaLabel(variant);
	const icon = getAlertIcon(variant);

	return (
		<div
			role="alert"
			data-slot="alert"
			aria-label={ariaLabel}
			className={alert({
				variant,
				className,
			})}
			{...props}
		>
			<div className={iconContainer({ variant })}>{icon}</div>
			<div className="flex flex-col gap-4 p-4">{children}</div>
		</div>
	);
};

type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement>;

const AlertTitle = ({ className, ...props }: AlertTitleProps) => {
	return <h2 data-slot="alert-title" className={cn('text-lg', className)} {...props} />;
};

type AlertBodyProps = React.HTMLAttributes<HTMLDivElement>;

const AlertBody = ({ className, ...props }: AlertBodyProps) => {
	return <div data-slot="alert-content" className={cn('text-sm', className)} {...props} />;
};

Alert.Title = AlertTitle;
Alert.Body = AlertBody;

export { Alert };
